import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["alt", "title", "src", "preloader", "width", "height", "sources", "theme", "borderRadius", "display", "objectFit", "onLoad", "onError", "loading"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as React from 'react';
import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import { EImageSizeType } from '../../types';
import { clsx } from 'clsx';
import { Spinner } from '../../../loader';
import { getTheme } from '../../themes';
import { Error } from '../../internal/Error';
import { Picture } from '../../internal/Picture';
import styles from '../../styles/image.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const getImageSizeType = el => {
  const width = el.getBoundingClientRect().width;
  if (width >= 320) {
    return EImageSizeType.L;
  }
  if (width < 40) {
    return EImageSizeType.NONE;
  }
  return EImageSizeType.S;
};
const ImageComponent = /*#__PURE__*/React.forwardRef(function Image(props, ref) {
  const {
      alt,
      title,
      src,
      preloader = true,
      width,
      height,
      sources = [],
      theme = 'light',
      borderRadius = '4px',
      display,
      objectFit,
      onLoad,
      onError,
      loading
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const imageEl = React.useRef(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [imageSizeType, setImageSizeType] = React.useState(EImageSizeType.NONE);
  const fulfilled = !isLoading && !error;
  const themeClasses = getTheme(theme);
  const handleError = e => {
    setIsLoading(false);
    setError(true);
    if (onError) {
      onError(e);
    }
  };
  const handleLoad = e => {
    setIsLoading(false);
    if (onLoad) {
      onLoad(e);
    }
  };
  useIsomorphicLayoutEffect(() => {
    if (imageEl.current.complete) {
      setIsLoading(false);
      if (imageEl.current.naturalWidth === 0) {
        setError(true);
      }
    }
    setImageSizeType(getImageSizeType(imageEl.current));
  }, []);
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({}, rest), {}, {
    ref: ref,
    className: clsx(styles['container'], themeClasses['container']),
    style: {
      width,
      height,
      borderRadius,
      display
    },
    children: [!fulfilled && /*#__PURE__*/_jsx("div", {
      className: clsx(styles['layout'], themeClasses['layout'])
    }), isLoading && preloader && /*#__PURE__*/_jsx("span", {
      className: clsx(styles['spinner'], themeClasses['spinner']),
      children: /*#__PURE__*/_jsx(Spinner, {
        color: "current_color",
        size: 16
      })
    }), error && !!imageSizeType && /*#__PURE__*/_jsx(Error, {
      size: imageSizeType,
      className: clsx(styles['error'], themeClasses['error'])
    }), /*#__PURE__*/_jsx(Picture, {
      className: clsx(styles['picture'], {
        [styles['picture_visible']]: fulfilled
      }),
      alt: alt,
      title: title,
      size: imageSizeType,
      objectFit: objectFit,
      onLoad: handleLoad,
      sources: sources,
      onError: handleError,
      src: src,
      ref: imageEl,
      loading: loading
    })]
  }));
});
ImageComponent.displayName = 'Image';

/**
 * Компонента изображения. <br />
 * Если в компонент предаеться список ссылкок, не забывайте последним элементом списка  <br />
 * передавать ссылку на картинку которая будет работать во всех браузерах.
 *
 * [Ссылка на макет](https://www.figma.com/design/4UM8zoowH9YGMM7fSK0rhD/%F0%9F%8C%90-Web-Components?node-id=33907-23224)
 */
export const Image = /*#__PURE__*/React.memo(ImageComponent);